import { buildClasses } from "../utils/buildClasses";

// The marketing pages run on their own type scale -- larger and airier than the
// app chrome that rye/RyeButton is sized for -- so they get their own button
// rather than stretching the design system to fit one page.
export function WebsiteButton(props: {
  children: string;
  onClick: () => void;
  // "filled" is the purple call to action, "plain" is the quieter text button,
  // and "nav" is the quietest: lighter weight and color, for header links.
  variant?: "filled" | "plain" | "nav";
  // "sm" suits the header, "md" and "lg" the page-level calls to action.
  size?: "sm" | "md" | "lg";
  // A trailing arrow that nudges right on hover, for a short label that
  // needs more presence.
  arrow?: boolean;
}): JSX.Element {
  const variant = props.variant ?? "filled";
  const size = props.size ?? "md";
  // At the header's small size, a full medium weight read heavier than the
  // surrounding nav, so the small buttons sit between regular and medium.
  const weight = size === "sm" ? "font-[450]" : "font-medium";

  return (
    <button
      className={buildClasses(
        "group",
        "inline-flex",
        "items-center",
        "gap-2",
        "rounded-full",
        "transition-colors",
        {
          switch: size,
          cases: new Map([
            ["sm", buildClasses("h-9", "px-3", "text-sm")],
            ["md", buildClasses("px-5", "py-2.5")],
            ["lg", buildClasses("px-7", "py-3", "text-lg")],
          ]),
        },
        {
          switch: variant,
          cases: new Map([
            [
              "filled",
              buildClasses(
                "bg-purple",
                weight,
                "text-white",
                "hover:bg-purple-600",
              ),
            ],
            [
              "plain",
              buildClasses(
                weight,
                "text-on-surface-800",
                "hover:bg-surface-300",
              ),
            ],
            [
              "nav",
              buildClasses(
                "font-normal",
                "text-on-surface-500",
                "hover:bg-surface-300",
                "hover:text-on-surface-800",
              ),
            ],
          ]),
        },
      )}
      onClick={props.onClick}
    >
      {props.children}
      {props.arrow === true && (
        // Inline SVG rather than a Material Symbols icon: the icon font loads
        // late, and the prerendered page would show its name as text until
        // then.
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={buildClasses(
            "h-[1.1em]",
            "w-[1.1em]",
            "transition-transform",
            "group-hover:translate-x-0.5",
            "motion-reduce:transition-none",
          )}
        >
          <path d="M4 10h12M11 5l5 5-5 5" />
        </svg>
      )}
    </button>
  );
}
